import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Avatar,
} from '@mui/material';
import {
  Search as SearchIcon,
  LocalShipping as ShippingIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://ishqnamaart.in/api';

const PublicTrackOrder = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [trackingId, setTrackingId] = useState(searchParams.get('id') || '');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  // Auto-fetch if ID is in URL
  useEffect(() => {
    const urlId = searchParams.get('id');
    if (urlId) {
      setTrackingId(urlId);
      fetchTrackingData(urlId);
    }
  }, [searchParams]);

  const fetchTrackingData = async (id) => {
    if (!id || !id.trim()) {
      setError('Please enter a tracking ID or order ID');
      return;
    }

    setLoading(true);
    setError('');
    setResult(null);

    try {
      const identifier = id.trim();
      let response;

      // Determine if it's an Ishqnama order ID or Shiprocket AWB
      if (identifier.toUpperCase().startsWith('ISHQ')) {
        // Ishqnama order ID - call internal API first to get tracking_id
        try {
          const orderResponse = await axios.get(
            `${API_BASE_URL}/order_status.php?orderId=${encodeURIComponent(identifier)}`
          );
          
          if (orderResponse.data.success && orderResponse.data.data) {
            const orderData = orderResponse.data.data;
            const trackingId = orderData.tracking_id;
            
            if (!trackingId) {
              setError('Tracking ID not available for this order');
              setLoading(false);
              return;
            }
            
            // Now call Shiprocket API with the tracking ID
            const trackingResponse = await axios.get(
              `${API_BASE_URL}/shiprocket_tracking.php?action=track&awb=${encodeURIComponent(trackingId)}`
            );
            
            if (trackingResponse.data.success && trackingResponse.data.data) {
              // The API response structure is: { success: true, data: { tracking_data: {...} } }
              const apiData = trackingResponse.data.data;
              
              // Wrap the response to match expected format
              response = {
                data: {
                  order_id: identifier,
                  tracking_id: trackingId,
                  courier_name: orderData.courier_name || 'Shiprocket',
                  shiprocket_data: {
                    tracking_data: apiData.tracking_data || apiData
                  }
                }
              };
            } else {
              setError(trackingResponse.data.error || 'Tracking information not found');
              setLoading(false);
              return;
            }
          } else {
            setError(orderResponse.data.error || 'Order not found');
            setLoading(false);
            return;
          }
        } catch (orderErr) {
          console.error('Order status error:', orderErr);
          setError(
            orderErr.response?.data?.error || 
            'Unable to fetch order information. Please try again.'
          );
          setLoading(false);
          return;
        }
      } else {
        // Direct Shiprocket AWB - call Shiprocket API directly
        const trackingResponse = await axios.get(
          `${API_BASE_URL}/shiprocket_tracking.php?action=track&awb=${encodeURIComponent(identifier)}`
        );
        
        if (trackingResponse.data.success && trackingResponse.data.data) {
          // For direct AWB, the response from API is { tracking_data: {...} }
          // Check if it's already wrapped or direct
          const apiData = trackingResponse.data.data;
          
          // If it has tracking_data at root, use it directly
          // Otherwise, wrap it
          if (apiData.tracking_data) {
            response = {
              data: {
                tracking_id: identifier,
                courier_name: 'Shiprocket',
                shiprocket_data: {
                  tracking_data: apiData.tracking_data
                }
              }
            };
          } else {
            // Response is already in tracking_data format
            response = {
              data: {
                tracking_id: identifier,
                courier_name: 'Shiprocket',
                shiprocket_data: {
                  tracking_data: apiData
                }
              }
            };
          }
        } else {
          setError(trackingResponse.data.error || 'Tracking information not found');
          setLoading(false);
          return;
        }
      }
      
      if (response && response.data) {
        setResult(response.data);
        // Update URL with tracking ID
        setSearchParams({ id: identifier });
      } else {
        setError('Tracking information not found');
      }
    } catch (err) {
      console.error('Tracking error:', err);
      setError(
        err.response?.data?.error || 
        err.message || 
        'Unable to fetch tracking information. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    fetchTrackingData(trackingId);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  const getStatusColor = (status) => {
    const statusLower = (status || '').toLowerCase();
    if (statusLower.includes('delivered')) return '#4caf50';
    if (statusLower.includes('out for delivery') || statusLower.includes('ofd')) return '#ff9800';
    if (statusLower.includes('in transit') || statusLower.includes('transit')) return '#2196f3';
    if (statusLower.includes('picked') || statusLower.includes('pickup')) return '#9c27b0';
    if (statusLower.includes('canceled') || statusLower.includes('cancelled')) return '#f44336';
    return '#757575';
  };

  const getStatusIcon = (status) => {
    const statusLower = (status || '').toLowerCase();
    if (statusLower.includes('delivered')) return <CheckCircleIcon />;
    if (statusLower.includes('out for delivery') || statusLower.includes('ofd')) return <ShippingIcon />;
    if (statusLower.includes('in transit') || statusLower.includes('transit')) return <ShippingIcon />;
    if (statusLower.includes('picked') || statusLower.includes('pickup')) return <ScheduleIcon />;
    return <ShippingIcon />;
  };

  // Extract tracking data from response
  // Handle both formats:
  // 1. ISHQ orders: { shiprocket_data: { tracking_data: {...} } }
  // 2. Direct AWB: { tracking_data: {...} } (wrapped in shiprocket_data.tracking_data)
  const trackingData = result?.shiprocket_data?.tracking_data || 
                       result?.tracking_data ||
                       result?.shiprocket_data?.[Object.keys(result?.shiprocket_data || {})[0]]?.tracking_data;
  const shipmentTrack = trackingData?.shipment_track?.[0] || {};
  const activities = trackingData?.shipment_track_activities || [];
  const currentStatus = shipmentTrack?.current_status || trackingData?.current_status || 'Pending';
  const awbCode = shipmentTrack?.awb_code || result?.tracking_id || '';

  return (
    <>
      <Helmet>
        <title>Track Your Order - Ishqnama Art</title>
        <meta name="description" content="Track your order status with real-time updates from Ishqnama Art" />
      </Helmet>

      <Box sx={{ 
        minHeight: '100vh', 
        background: 'white',
        py: { xs: 3, md: 6 }
      }}>
        <Container maxWidth="md">
          {/* Logo and Header */}
          <Box sx={{ 
            textAlign: 'center', 
            mb: { xs: 3, md: 4 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1.5
          }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                fontSize: { xs: '1.25rem', md: '1.5rem' },
                letterSpacing: '0.5px',
              }}
            >
              Track Your Order
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontSize: { xs: '0.813rem', md: '0.875rem' },
                maxWidth: 500,
                mt: 0.5,
              }}
            >
              Enter your Tracking ID or Order ID to get real-time updates
            </Typography>
          </Box>

          {/* Search Form */}
          {!result && (
            <Card
              sx={{
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                borderRadius: 3,
                mb: 4,
              }}
            >
              <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                <Box
                  component="form"
                  onSubmit={handleSubmit}
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 2,
                  }}
                >
                  <TextField
                    fullWidth
                    placeholder="Enter Tracking ID or Order ID (e.g., SF1414071115SPF or ISHQ0000297)"
                    value={trackingId}
                    onChange={(e) => setTrackingId(e.target.value)}
                    disabled={loading}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading || !trackingId.trim()}
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                    sx={{
                      minWidth: { xs: '100%', sm: 180 },
                      py: 1.5,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #FF6B9D 0%, #E91E63 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #E91E63 0%, #C2185B 100%)',
                      },
                    }}
                  >
                    {loading ? 'Tracking...' : 'Track Order'}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Error Message */}
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3,
                borderRadius: 2,
              }}
              onClose={() => setError('')}
            >
              {error}
            </Alert>
          )}

          {/* Tracking Results */}
          {result && trackingData && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Current Status Card */}
              <Card
                sx={{
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                  borderRadius: 3,
                  border: `2px solid ${getStatusColor(currentStatus)}`,
                }}
              >
                <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar
                      sx={{
                        bgcolor: getStatusColor(currentStatus),
                        width: { xs: 50, md: 60 },
                        height: { xs: 50, md: 60 },
                      }}
                    >
                      {getStatusIcon(currentStatus)}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: 'text.primary',
                          mb: 0.5,
                        }}
                      >
                        {currentStatus}
                      </Typography>
                      {awbCode && (
                        <Typography variant="body2" color="text.secondary">
                          AWB: {awbCode}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {/* Key Information */}
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                    gap: 2,
                    mt: 3,
                  }}>
                    {shipmentTrack?.pickup_date && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Pickup Date
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {formatDate(shipmentTrack.pickup_date)}
                        </Typography>
                      </Box>
                    )}
                    {shipmentTrack?.delivered_date && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Delivered Date
                        </Typography>
                        <Typography variant="body2" fontWeight={600} color="#4caf50">
                          {formatDate(shipmentTrack.delivered_date)}
                        </Typography>
                      </Box>
                    )}
                    {shipmentTrack?.edd && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Estimated Delivery
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {formatDate(shipmentTrack.edd)}
                        </Typography>
                      </Box>
                    )}
                    {shipmentTrack?.destination && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Destination
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {shipmentTrack.destination}
                        </Typography>
                      </Box>
                    )}
                    {shipmentTrack?.origin && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Origin
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {shipmentTrack.origin}
                        </Typography>
                      </Box>
                    )}
                    {shipmentTrack?.consignee_name && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Consignee
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {shipmentTrack.consignee_name}
                        </Typography>
                      </Box>
                    )}
                    {shipmentTrack?.courier_name && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Courier
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {shipmentTrack.courier_name}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Delivery Agent Details - Show when Out for Delivery */}
                  {shipmentTrack?.courier_agent_details && (
                    <Box sx={{ 
                      mt: 3, 
                      pt: 3, 
                      borderTop: '1px solid rgba(0, 0, 0, 0.1)',
                      background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.05) 0%, rgba(255, 152, 0, 0.02) 100%)',
                      borderRadius: 2,
                      p: 2,
                    }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 700,
                          color: '#ff9800',
                          mb: 2,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <ShippingIcon sx={{ fontSize: '1.2rem' }} />
                        Delivery Agent Information
                      </Typography>
                      <Box sx={{ 
                        display: 'grid', 
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                        gap: 2,
                      }}>
                        {shipmentTrack.courier_agent_details.delivery_boy_name && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonIcon sx={{ color: '#ff9800', fontSize: '1.2rem' }} />
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Delivery Agent
                              </Typography>
                              <Typography variant="body2" fontWeight={600}>
                                {shipmentTrack.courier_agent_details.delivery_boy_name}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                        {shipmentTrack.courier_agent_details.delivery_boy_phone && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PhoneIcon sx={{ color: '#ff9800', fontSize: '1.2rem' }} />
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Contact Number
                              </Typography>
                              <Typography 
                                variant="body2" 
                                fontWeight={600}
                                component="a"
                                href={`tel:${shipmentTrack.courier_agent_details.delivery_boy_phone}`}
                                sx={{
                                  color: '#1976d2',
                                  textDecoration: 'none',
                                  '&:hover': {
                                    textDecoration: 'underline',
                                  },
                                }}
                              >
                                {shipmentTrack.courier_agent_details.delivery_boy_phone}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                        {shipmentTrack.courier_agent_details.ofd_time && (
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Out for Delivery Time
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {formatDate(shipmentTrack.courier_agent_details.ofd_time)}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Card>

              {/* Tracking Timeline */}
              {activities && activities.length > 0 && (
                <Card
                  sx={{
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                    borderRadius: 3,
                  }}
                >
                  <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        mb: 3,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <LocationIcon sx={{ color: '#FF6B9D' }} />
                      Tracking History
                    </Typography>
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      gap: 2,
                      maxHeight: { xs: '400px', md: '500px' },
                      overflowY: 'auto',
                      pr: 1,
                      '&::-webkit-scrollbar': {
                        width: '6px',
                      },
                      '&::-webkit-scrollbar-track': {
                        background: '#f1f1f1',
                        borderRadius: '10px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: '#FF6B9D',
                        borderRadius: '10px',
                      },
                    }}>
                      {activities.map((activity, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: 'flex',
                            gap: 2,
                            position: 'relative',
                            pl: { xs: 3, md: 4 },
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              left: { xs: '8px', md: '12px' },
                              top: '24px',
                              bottom: '-16px',
                              width: '2px',
                              background: index === activities.length - 1 ? 'transparent' : '#e0e0e0',
                            },
                          }}
                        >
                          <Avatar
                            sx={{
                              bgcolor: index === 0 ? getStatusColor(activity.status) : '#e0e0e0',
                              width: { xs: 32, md: 40 },
                              height: { xs: 32, md: 40 },
                              fontSize: '0.875rem',
                            }}
                          >
                            {index === 0 ? '✓' : index + 1}
                          </Avatar>
                          <Box sx={{ flex: 1, pb: 2 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: index === 0 ? 700 : 600,
                                color: 'text.primary',
                                mb: 0.5,
                              }}
                            >
                              {activity['sr-status-label'] || activity.activity || activity.status}
                            </Typography>
                            {activity.location && (
                              <Typography variant="caption" color="text.secondary">
                                {activity.location}
                              </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                              {formatDate(activity.date)}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              )}

              {/* Track Another Order Button */}
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setResult(null);
                    setTrackingId('');
                    setError('');
                    setSearchParams({});
                  }}
                  sx={{
                    borderRadius: 2,
                    borderColor: '#FF6B9D',
                    color: '#FF6B9D',
                    '&:hover': {
                      borderColor: '#E91E63',
                      backgroundColor: 'rgba(255, 107, 157, 0.08)',
                    },
                  }}
                >
                  Track Another Order
                </Button>
              </Box>
            </Box>
          )}

          {/* No Tracking Data Message */}
          {result && !trackingData && (
            <Card
              sx={{
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                borderRadius: 3,
                textAlign: 'center',
                p: 4,
              }}
            >
              <Alert severity="info" sx={{ mb: 2 }}>
                Tracking information is not available yet. Please check back later.
              </Alert>
              <Button
                variant="outlined"
                onClick={() => {
                  setResult(null);
                  setTrackingId('');
                  setError('');
                  setSearchParams({});
                }}
                sx={{
                  borderRadius: 2,
                  borderColor: '#FF6B9D',
                  color: '#FF6B9D',
                  '&:hover': {
                    borderColor: '#E91E63',
                    backgroundColor: 'rgba(255, 107, 157, 0.08)',
                  },
                }}
              >
                Track Another Order
              </Button>
            </Card>
          )}
        </Container>
      </Box>
    </>
  );
};

export default PublicTrackOrder;

